import React, { useState } from 'react';
import { GitMerge, X, CheckCircle2, Monitor, Laptop, Users } from 'lucide-react';
import type { SyncConflict, FieldConflict } from '../../../types';
import { formatFieldValue } from '../utils/threeWayMerge';

interface Props {
  conflict: SyncConflict;
  isSharedBoard?: boolean;
  onResolve: (conflict: SyncConflict, resolvedFields: FieldConflict[]) => void;
  onAbort: (conflict: SyncConflict) => void;
  onSendToVote?: (conflict: SyncConflict) => void;
}

export const ConflictResolutionModal: React.FC<Props> = ({ conflict, isSharedBoard, onResolve, onAbort, onSendToVote }) => {
  const [fields, setFields] = useState<FieldConflict[]>(
    conflict.fields.map((f) => ({ ...f, resolution: 'server' as const })),
  );

  const allResolved = fields.every((f) => f.resolution !== null);

  const setResolution = (fieldName: string, resolution: 'local' | 'server') => {
    setFields((prev) =>
      prev.map((f) => (f.field === fieldName ? { ...f, resolution } : f)),
    );
  };

  const setAll = (resolution: 'local' | 'server') => {
    setFields((prev) => prev.map((f) => ({ ...f, resolution })));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-800">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <GitMerge className="w-5 h-5 text-amber-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-slate-800 dark:text-slate-100 text-base">Sync conflict</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
              <span className="capitalize">{conflict.entityType}</span>:{' '}
              <span className="font-medium text-slate-700 dark:text-slate-300">{conflict.entityTitle}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={() => onAbort(conflict)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="Abort these changes"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-3 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800">
          <p className="text-xs text-amber-800 dark:text-amber-300">
            This item was edited both offline and on the server. Choose which version to keep for each field.
          </p>
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={() => setAll('local')}
              className="px-2.5 py-1 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              Keep all mine
            </button>
            <button
              type="button"
              onClick={() => setAll('server')}
              className="px-2.5 py-1 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              Keep all server
            </button>
          </div>
        </div>

        {/* Field list */}
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
          {fields.map((f) => (
            <div key={f.field} className="p-4 space-y-3">
              <p className="text-xs font-bold text-slate-700 dark:text-slate-300">{f.label}</p>

              <div className="grid grid-cols-2 gap-3">
                {/* Local version */}
                <button
                  type="button"
                  onClick={() => setResolution(f.field, 'local')}
                  className={`text-left p-3 rounded-lg border-2 transition-all ${
                    f.resolution === 'local'
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40'
                      : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Laptop className="w-3.5 h-3.5 text-indigo-500" />
                    <span className="text-[11px] font-semibold text-indigo-700 dark:text-indigo-400">Your offline version</span>
                    {f.resolution === 'local' && (
                      <CheckCircle2 className="w-3.5 h-3.5 text-indigo-500 ml-auto" />
                    )}
                  </div>
                  <p className="text-xs text-slate-700 dark:text-slate-300 break-words line-clamp-4">
                    {formatFieldValue(f.localValue)}
                  </p>
                </button>

                {/* Server version */}
                <button
                  type="button"
                  onClick={() => setResolution(f.field, 'server')}
                  className={`text-left p-3 rounded-lg border-2 transition-all ${
                    f.resolution === 'server'
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40'
                      : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-700'
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Monitor className="w-3.5 h-3.5 text-emerald-500" />
                    <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">Server version</span>
                    {f.resolution === 'server' && (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 ml-auto" />
                    )}
                  </div>
                  <p className="text-xs text-slate-700 dark:text-slate-300 break-words line-clamp-4">
                    {formatFieldValue(f.serverValue)}
                  </p>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={() => onAbort(conflict)}
            className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 text-sm font-semibold transition-colors"
          >
            Abort changes
          </button>
          <div className="flex items-center gap-2">
            {isSharedBoard && onSendToVote && (
              <button
                type="button"
                onClick={() => onSendToVote(conflict)}
                className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold transition-colors flex items-center gap-1.5"
                title="Let your team vote on how to resolve this conflict"
              >
                <Users className="w-4 h-4" />
                Send to team vote
              </button>
            )}
            <button
              type="button"
              disabled={!allResolved}
              onClick={() => onResolve(conflict, fields)}
              className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold transition-colors"
            >
              Apply resolution
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
